import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "#/components/common/Button";
import { ConfirmDialog } from "#/components/common/ConfirmDialog";
import { ErrorState } from "#/components/common/ErrorState";
import { PageLoader } from "#/components/common/PageLoader";
import { StatusBadge } from "#/components/common/StatusBadge";
import { SummaryCard } from "#/components/common/SummaryCard";
import { TextInput } from "#/components/common/TextInput";
import { DataPagination } from "#/components/data-table/DataPagination";
import { DataTable, type ColumnDef } from "#/components/data-table/DataTable";
import { useDeleteRole, useRoles } from "../hooks";
import type { RoleResponse } from "../types";

export default function RolesPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [pendingDelete, setPendingDelete] = useState<RoleResponse | null>(null);

  const rolesQuery = useRoles();
  const deleteRole = useDeleteRole();

  const rows = useMemo(() => {
    const allRoles = rolesQuery.data ?? [];
    const q = search.trim().toLowerCase();

    if (!q) return allRoles;

    return allRoles.filter((role) =>
      role.name.toLowerCase().includes(q) ||
      role.code.toLowerCase().includes(q) ||
      (role.description ?? "").toLowerCase().includes(q)
    );
  }, [rolesQuery.data, search]);

  const pagedRows = useMemo(() => {
    const start = page * size;
    return rows.slice(start, start + size);
  }, [rows, page, size]);

  const totalPages = Math.max(1, Math.ceil(rows.length / size));
  const activeCount = rows.filter((item) => item.active).length;
  const totalRoles = rolesQuery.data?.length ?? 0;

  const columns = useMemo<ColumnDef<RoleResponse>[]>(() => [
    {
      key: "name",
      header: "Role",
      render: (row) => (
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{row.name}</p>
          <p className="text-xs text-slate-500">{row.code}</p>
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (row) => (
        <span className="text-sm text-slate-600">
          {row.description || "-"}
        </span>
      ),
    },
    {
      key: "permissionCodes",
      header: "Permissions",
      render: (row) => String(row.permissionCodes?.length ?? 0),
    },
    {
      key: "active",
      header: "Status",
      render: (row) => (
        <StatusBadge
          label={row.active ? "Active" : "Inactive"}
          tone={row.active ? "success" : "neutral"}
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <div className="flex flex-wrap gap-3">
          <Link to={`/admin/roles/${row.id}`} className="text-sm font-medium text-emerald-700 hover:underline">
            View
          </Link>

          <Link to={`/admin/roles/${row.id}/edit`} className="text-sm font-medium text-blue-700 hover:underline">
            Edit
          </Link>

          {row.code !== "SUPER_ADMIN" ? (
            <button
              type="button"
              className="text-sm font-medium text-red-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              disabled={deleteRole.isPending}
              onClick={() => setPendingDelete(row)}
            >
              Delete
            </button>
          ) : null}
        </div>
      ),
    },
  ], [deleteRole]);

  if (rolesQuery.isLoading) return <PageLoader label="Loading roles..." />;

  if (rolesQuery.isError) {
    return (
      <ErrorState
        title="Failed to load roles"
        message={
          rolesQuery.error instanceof Error
            ? rolesQuery.error.message
            : "Could not load roles."
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Roles</h1>
        </div>

        <Link to="/admin/roles/new">
          <Button>Create role</Button>
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard title="Total roles" value={totalRoles} />
        <SummaryCard title="Filtered roles" value={rows.length} />
        <SummaryCard title="Active roles" value={activeCount} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <TextInput
          label="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search by role name, code or description"
          className="md:max-w-sm"
        />
      </div>

      <DataTable
        data={pagedRows}
        columns={columns}
        emptyTitle="No roles found"
        emptyDescription="Try a different search or create a new role."
      />

      <DataPagination
        page={page}
        totalPages={totalPages}
        totalElements={rows.length}
        size={size}
        onPageChange={setPage}
        onSizeChange={(nextSize) => {
          setSize(nextSize);
          setPage(0);
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete Role"
        message={pendingDelete ? `Delete role "${pendingDelete.name}"? This cannot be undone.` : ""}
        confirmText="Delete"
        busy={deleteRole.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteRole.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}